"use client";

import { memo, useMemo } from "react";
import type { ThoughtReassignData } from "@/state/globalStoreSlices/userChoices";
import { useSelectionState, useUserChoices } from "@/state/globalStore";
import { showWarningToast } from "@/shared/feedback";
import GenericActionBar from "@/shared/ui/actions/GenericActionBar";
import type { GenericActionBarAction } from "@/shared/ui/actions/actionBar.types";
import { PROFILE_SHAPING_LABELS } from "@/features/profile-shaping/config/actions";
import type {
  MindstringPrototype,
  Situation,
  SituationCategory,
} from "@/generated/fastapi";
import {
  useSituationAccessors,
  useSituationsQuery,
} from "@/features/situations";
import { useStudioHandlersContext } from "../contexts/StudioHandlersProvider";
import { useStudioConfirmationContext } from "../contexts/StudioConfirmationProvider";
import useStudioProfileExport from "../hooks/useStudioProfileExport";

const PROTOTYPE_CATEGORY_MAP: Record<MindstringPrototype, SituationCategory> = {
  job_activity: "occupation",
  learning_outcome: "education",
  workplace_characteristic: "preference",
  individual_description: "development",
};

function situationSupportsPrototype(
  situation: Situation | undefined,
  prototype: MindstringPrototype,
) {
  if (!situation) {
    return false;
  }

  return (
    situation.prototypes.includes(prototype) ||
    situation.categories.includes(PROTOTYPE_CATEGORY_MAP[prototype])
  );
}

function formatSelectionPart(
  count: number,
  singular: string,
  plural = `${singular}s`,
) {
  return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * Centralized ActionBar management for Studio views
 * Renders the confirmed profile workspace action bar.
 */
function StudioActionBarsInner() {
  const {
    selectedThoughts,
    selectedThoughtsFree,
    selectedSituations,
    countSelectedThoughts,
    countSelectedSituations,
    getSelectedThoughtIds,
    getSelectedSituationIds,
  } = useSelectionState();
  const { thoughtHandlers, situationHandlers } = useStudioHandlersContext();
  const {
    activeStudioGrouping,
    activePrototypeCategory,
    activeProfileVersion,
    isSelectedProfile,
    setInterimState,
    resetSelectedProfile,
  } = useUserChoices();
  const situationsQuery = useSituationsQuery();
  const { situations } = useSituationAccessors(situationsQuery.data);
  const { isGenerating, handleDownloadResumeClick } = useStudioProfileExport();
  const { confirmSelectedSelectionDeletion } = useStudioConfirmationContext();
  const selectedSituationsCount = countSelectedSituations();
  const selectedSituationIds = getSelectedSituationIds();
  const selectedSituationObjects = selectedSituationIds.map((situationId) =>
    situations.find((situation) => situation.id === situationId),
  );
  const selectedSituationIdsForActivePrototype = selectedSituationObjects
    .filter((situation) =>
      situationSupportsPrototype(situation, activePrototypeCategory),
    )
    .map((situation) => situation?.id)
    .filter((situationId): situationId is string => Boolean(situationId));
  const selectedSituationOutputSettingsBlocked =
    selectedSituationIdsForActivePrototype.length === 0;
  const selectedSituationOutputSettingsScopeLabel =
    selectedSituationIdsForActivePrototype.length === selectedSituationsCount
      ? "selected situations"
      : "selected situations in the active perspective";
  const selectedThoughtPrototypes = Array.from(
    new Set(selectedThoughts.map((selection) => selection.prototype)),
  );
  const selectedPrototypeCategoriesCount = useMemo(() => {
    const selectedPrototypes = new Set<MindstringPrototype>();

    selectedThoughts.forEach((selection) => {
      selectedPrototypes.add(selection.prototype);
    });
    selectedThoughtsFree.forEach((selection) => {
      selectedPrototypes.add(selection.prototype);
    });
    selectedSituations.forEach((selection) => {
      if (selection.situations_ids.length > 0) {
        selectedPrototypes.add(selection.prototype);
      }
    });

    return selectedPrototypes.size;
  }, [selectedSituations, selectedThoughts, selectedThoughtsFree]);
  const selectedThoughtValues: ThoughtReassignData[] = useMemo(() => {
    const selectedThoughtMetadata = new Map<string, ThoughtReassignData>();

    selectedThoughts.forEach((selection) => {
      selection.thoughts_ids.forEach((thoughtId) => {
        selectedThoughtMetadata.set(thoughtId, {
          objectID: thoughtId,
          situation_id: selection.situation_id,
          prototype: selection.prototype,
        });
      });
    });

    selectedThoughtsFree.forEach((selection) => {
      selection.thoughts_ids.forEach((thoughtId) => {
        if (selectedThoughtMetadata.has(thoughtId)) {
          return;
        }

        selectedThoughtMetadata.set(thoughtId, {
          objectID: thoughtId,
          situation_id: null,
          prototype: selection.prototype,
        });
      });
    });

    return Array.from(selectedThoughtMetadata.values());
  }, [selectedThoughts, selectedThoughtsFree]);

  const selectedThoughtsCount = countSelectedThoughts();
  if (selectedThoughtsCount === 0 && selectedSituationsCount === 0) {
    return null;
  }

  const confirmedSelectionLabel = [
    formatSelectionPart(selectedThoughtsCount, "thought"),
    formatSelectionPart(selectedSituationsCount, "situation"),
    formatSelectionPart(
      selectedPrototypeCategoriesCount,
      "perspective",
      "perspectives",
    ),
  ].join(" @ ");

  const confirmedActions: GenericActionBarAction[] = [
    {
      key: "reflect",
      label: "Reflect",
      shortLabel: "Reflect",
      humanReadable: "Reflect on selected thoughts",
      icon: "reflect_thoughts",
      tone: "primary",
      priority: 1,
      mobileBehavior: "inline",
      hidden: selectedThoughtsCount === 0,
      onSelect: () => {
        setInterimState("reflect_thoughts", {
          isOpen: true,
          prototypeCategory: activePrototypeCategory,
        });
      },
    },
    {
      key: "output_settings",
      label: "Export settings",
      shortLabel: "Export",
      humanReadable: selectedSituationOutputSettingsBlocked
        ? "Select situations that support the active perspective before changing profile export settings"
        : `Adjust profile export settings for ${selectedSituationOutputSettingsScopeLabel}`,
      icon: "settings_print",
      priority: 3,
      hidden: selectedSituationsCount === 0,
      disabled: selectedSituationOutputSettingsBlocked,
      mobileBehavior: "inline",
      onSelect: () => {
        if (selectedSituationOutputSettingsBlocked) {
          showWarningToast(
            "Profile export settings can only be edited for selected situations that support the active perspective.",
          );
          return;
        }

        setInterimState("batch_situation_config", {
          isOpen: true,
          value: {
            prototype: activePrototypeCategory,
            situationIds: selectedSituationIdsForActivePrototype,
          },
        });
      },
    },
    {
      key: "extend_version",
      label: "Extend version",
      shortLabel: "Extend",
      humanReadable: activeProfileVersion
        ? `Extend active version ${activeProfileVersion.name}`
        : "Load or save a profile version before extending it",
      icon: "save",
      tone: "success",
      priority: 2,
      hidden: !activeProfileVersion,
      disabled: !isSelectedProfile,
      mobileBehavior: "inline",
      onSelect: () => {
        if (!activeProfileVersion) {
          return;
        }

        if (!isSelectedProfile) {
          showWarningToast(
            "Select confirmed profile material before extending a version.",
          );
          return;
        }

        setInterimState("manage_version", {
          isOpen: true,
          value: "extend_selection",
        });
      },
    },
    {
      key: "save_version",
      label: "Save version",
      shortLabel: "Save",
      humanReadable: isSelectedProfile
        ? "Save selected confirmed profile material as a version"
        : "Select confirmed profile material before saving a version",
      icon: "save",
      tone: "success",
      priority: 4,
      disabled: !isSelectedProfile,
      mobileBehavior: "overflow",
      onSelect: () => {
        if (!isSelectedProfile) {
          showWarningToast(
            "Select confirmed profile material before saving a version.",
          );
          return;
        }

        setInterimState("manage_version", {
          isOpen: true,
          value: "save_selection",
        });
      },
    },
    {
      key: "download_cv",
      label: "Download CV",
      shortLabel: "CV",
      humanReadable: isGenerating
        ? "CV download is already being prepared"
        : isSelectedProfile
          ? "Download a CV from the current Studio selection"
          : "Select confirmed profile material before exporting a CV",
      icon: "download",
      tone: "success",
      priority: 5,
      disabled: !isSelectedProfile || isGenerating,
      mobileBehavior: "overflow",
      onSelect: async () => {
        if (!isSelectedProfile) {
          showWarningToast(
            "Select confirmed profile material before exporting a CV.",
          );
          return;
        }

        await handleDownloadResumeClick(activeStudioGrouping);
      },
    },
    {
      key: "copy",
      label: "Copy",
      shortLabel: "Copy",
      humanReadable: "Copy selected thoughts to clipboard",
      icon: "copy_clipboard",
      priority: 6,
      hidden: selectedThoughtsCount === 0,
      mobileBehavior: "overflow",
      onSelect: async () =>
        await thoughtHandlers.handleCopyMindstringsToClipboard(
          getSelectedThoughtIds(),
          "thoughts",
        ),
    },
    {
      key: "reassign",
      label: PROFILE_SHAPING_LABELS.anchorToSituation,
      shortLabel: "Anchor",
      humanReadable: "Anchor selected thoughts to a situation",
      icon: "anchor",
      priority: 7,
      hidden: selectedThoughtValues.length === 0,
      mobileBehavior: "overflow",
      onSelect: () => {
        setInterimState("reassign_thought", {
          isOpen: true,
          values: selectedThoughtValues,
        });
      },
    },
    {
      key: "reset",
      label: "Reset",
      shortLabel: "Reset",
      humanReadable: "Reset reflections for selected thoughts",
      icon: "reset",
      tone: "warning",
      priority: 8,
      hidden: selectedThoughtsCount === 0,
      mobileBehavior: "overflow",
      onSelect: async () =>
        await thoughtHandlers.handlSelectedThoughtsResetReflectionsClick({
          thoughts_id: getSelectedThoughtIds(),
        }),
    },
    {
      key: "unselect_visible",
      label: "Unselect visible",
      shortLabel: "Unselect",
      humanReadable:
        "Unselect visible selected items in the active perspective",
      icon: "unselect",
      tone: "warning",
      priority: 99,
      mobileBehavior: "overflow",
      onSelect: () =>
        situationHandlers.handleSelectedSituationsUnselectClick(
          activePrototypeCategory,
        ),
    },
    {
      key: "unselect_all",
      label: "Unselect all",
      shortLabel: "Clear all",
      humanReadable:
        "Unselect all selected profile material across all perspectives",
      icon: "clear",
      tone: "warning",
      priority: 99,
      mobileBehavior: "overflow",
      onSelect: resetSelectedProfile,
    },
    {
      key: "delete_selection",
      label: "Delete",
      shortLabel: "Delete",
      humanReadable: "Delete selected items permanently",
      icon: "delete_permanent",
      tone: "error",
      priority: 9,
      separatorAfter: true,
      mobileBehavior: "overflow",
      onSelect: () => {
        confirmSelectedSelectionDeletion.onOpen(async (deleteOptions) => {
          const selectedThoughtIdsForDeletion = getSelectedThoughtIds();
          const selectedSituationGroupsForDeletion = selectedSituations
            .map((selection) => ({
              prototype: selection.prototype,
              situationIds: [...selection.situations_ids],
            }))
            .filter((selection) => selection.situationIds.length > 0);

          if (
            deleteOptions?.deleteThoughts &&
            selectedThoughtIdsForDeletion.length > 0
          ) {
            await thoughtHandlers.handleSelectedThoughtsDeleteClick(
              {
                thought_ids: selectedThoughtIdsForDeletion,
                delete_mode: "permanent",
                sync_meili_index: "thoughts",
              },
              undefined,
              selectedThoughtPrototypes,
              { skipConfirmation: true },
            );
          }

          if (
            deleteOptions?.deleteSituations &&
            selectedSituationGroupsForDeletion.length > 0
          ) {
            for (const selectionGroup of selectedSituationGroupsForDeletion) {
              await situationHandlers.handleSituationsDeleteClick(
                {
                  situation_ids: selectionGroup.situationIds,
                  delete_mode: "permanent",
                  prototype: selectionGroup.prototype,
                  related_thoughts_action:
                    deleteOptions.relatedThoughtsChoice.action,
                  anchor_thoughts_to_situation_id:
                    deleteOptions.relatedThoughtsChoice.targetSituationId ??
                    null,
                },
                { skipConfirmation: true },
              );
            }
          }
        });
      },
    },
  ];

  return (
    <GenericActionBar
      selectedItemsCount={selectedThoughtsCount}
      selectedSituationsCount={selectedSituationsCount}
      selectionLabel={confirmedSelectionLabel}
      contextLabel={
        activeProfileVersion
          ? `Active version: ${activeProfileVersion.name}`
          : null
      }
      actions={confirmedActions}
    />
  );
}

// Memoize to prevent unnecessary re-renders
export default memo(StudioActionBarsInner);
